import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { useMemo } from "react";

import { usePrice } from "@/hooks";

dayjs.extend(isBetween);

export const useCalculatePrice = (params: {
  catalogMediumId: string;
  selectedConfigurationOptionsIds: Array<string>;
  quantity: number | null;
  numberOfVersions?: number;
  enabled?: boolean;
}) => {
  const { useGetPrices } = usePrice();

  const { data: getPricesQueryResult, isLoading } = useGetPrices({
    catalogMediumId: params.catalogMediumId,
    selectedConfigurationOptionsIds:
      params.selectedConfigurationOptionsIds.filter(
        (selectedConfigurationOptionId) =>
          selectedConfigurationOptionId !== undefined
      ),
    enabled: params.enabled,
  });

  // When the configuration changes, set the quantity's default value to the configuration's minimum quantity
  const suggestedMinimumQuantity = useMemo(() => {
    if (getPricesQueryResult?.data && getPricesQueryResult?.data?.length > 0) {
      const minimumQuantity = getPricesQueryResult?.data?.reduce(
        (currentMinQuantity, currentPriceConf) => {
          if (
            currentMinQuantity === 0 ||
            currentMinQuantity > currentPriceConf.minQuantity
          ) {
            return currentPriceConf.minQuantity;
          }
          return currentMinQuantity;
        },
        0
      );

      return minimumQuantity;
    }
  }, [getPricesQueryResult?.data]);

  // Compute valid prices configurations based on the current quantity
  const validPricesConfigurations = useMemo(() => {
    if (params.quantity === null) {
      return [];
    }

    return (
      getPricesQueryResult?.data?.filter((priceConfiguration) => {
        return (
          params.quantity &&
          params.quantity >= priceConfiguration.minQuantity &&
          params.quantity <= priceConfiguration.maxQuantity &&
          dayjs().isBetween(
            priceConfiguration.startDate,
            priceConfiguration.endDate,
            "day",
            "[]"
          ) &&
          (priceConfiguration.minVersion !== undefined &&
          priceConfiguration.minVersion !== null &&
          params.numberOfVersions !== undefined
            ? params.numberOfVersions >= priceConfiguration.minVersion
            : true)
        );
      }) || []
    );
  }, [getPricesQueryResult?.data, params.quantity, params.numberOfVersions]);

  // Now compute the total price based on a valid price configuration if it exists
  const totalPrice: number | undefined = useMemo(
    () =>
      validPricesConfigurations.length > 0 && params.quantity !== null
        ? (validPricesConfigurations[0].fixedPrice || 0) +
          validPricesConfigurations[0].unitPrice * params.quantity +
          (validPricesConfigurations[0].maxVersion !== undefined &&
          validPricesConfigurations[0].maxVersion !== null &&
          params.numberOfVersions !== undefined &&
          params.numberOfVersions > validPricesConfigurations[0].maxVersion
            ? (params.numberOfVersions -
                validPricesConfigurations[0].maxVersion) *
              validPricesConfigurations[0].pricePerAdditionalVersion
            : 0)
        : undefined,
    [validPricesConfigurations, params.quantity, params.numberOfVersions]
  );

  const savings: number | null | undefined = useMemo(
    () =>
      validPricesConfigurations.length > 0
        ? validPricesConfigurations[0].savings
        : null,
    [validPricesConfigurations]
  );

  // Now compute the production time based on a valid price configuration if it exists
  const productionTime: number | undefined = useMemo(() => {
    const productionTimeValue =
      validPricesConfigurations.length > 0
        ? validPricesConfigurations[0].productionTime
        : undefined;
    return productionTimeValue === null ? undefined : productionTimeValue;
  }, [validPricesConfigurations]);

  return {
    totalPrice,
    productionTime,
    savings,
    suggestedMinimumQuantity,
    isLoading,
    prices: getPricesQueryResult?.data,
  };
};
